/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ArchiveJournalEventButton from 'parog-web/components/archive-journal-event-button';
import LinkToForc from 'parog-web/components/link-to-forc';
import MedicsAccordion from 'parog-web/components/medics-accordion';
import type CareGiverAddedJournalEvent from 'parog-web/models/care-giver-added-journal-event';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import Dropdown from 'ui-kit/components/dropdown';

interface Signature {
  Args: {
    medic?: unknown;
    patient: Patient;
    careGivers?: CareGiverAddedJournalEvent[];
  };
}

export default class CareGiversSectionContentComponent extends Component<Signature> {
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @tracked isSaving = false;

  get alloLaboCareGiversWithPhone(): CareGiverAddedJournalEvent[] {
    return this.careGivers.filter(
      careGiver =>
        careGiver.role == 'analistique-laboratory' && Boolean(careGiver.phone)
    );
  }

  get careGivers() {
    return (
      this.args.careGivers || this.args.patient.careGiverAddedJournalEvents
    );
  }

  get selectedAlloLabo(): string {
    return (
      this.args.patient.referentLabUpdatedJournalEvent
        ?.careGiverAddedJournalEvent?.id || 'none'
    );
  }

  changeReferentLab = async (id: string | string[]): Promise<void> => {
    if (Array.isArray(id)) {
      throw new Error(
        'changeReferentLab: multiple selection is not supported here'
      );
    }
    this.isSaving = true;

    if (id == 'none' && this.args.patient.referentLabUpdatedJournalEvent) {
      await this.args.patient.referentLabUpdatedJournalEvent?.archive();
      this.args.patient.referentLabUpdatedJournalEvent?.unloadRecord();
    } else {
      const referentLabUpdatedJournalEvent =
        this.args.patient.referentLabUpdatedJournalEvent ||
        this.store.createRecord('referent-lab-updated-journal-event', {
          patient: this.args.patient,
          occurredAt: new Date()
        });

      referentLabUpdatedJournalEvent.careGiverAddedJournalEvent =
        this.store.peekRecord(
          'care-giver-added-journal-event',
          id
        ) as CareGiverAddedJournalEvent;
      await referentLabUpdatedJournalEvent.save();
    }
    this.isSaving = false;
  };

  <template>
    {{#if this.alloLaboCareGiversWithPhone.length}}
      <Dropdown
        @labelName='Laboratoire de référence'
        @onChange={{this.changeReferentLab}}
        @selected={{this.selectedAlloLabo}}
        @isReadOnly={{this.isSaving}}
        data-test-referent-lab
        as |dropdown|
      >
        {{#each
          this.alloLaboCareGiversWithPhone
          as |alloLaboCareGiverWithPhone|
        }}
          <dropdown.Item
            @key='{{alloLaboCareGiverWithPhone.id}}'
            @value='{{concat
              alloLaboCareGiverWithPhone.firstName
              " "
              alloLaboCareGiverWithPhone.lastName
            }}'
          />
        {{/each}}
        <dropdown.Item @key='none' @value='Aucun' />
      </Dropdown>
    {{/if}}

    {{#if this.careGivers}}
      <MedicsAccordion
        @isCareGiver={{true}}
        {{! @glint-expect-error }}
        @medics={{this.careGivers}}
        @medicTypePrefix='careGiver'
        as |accordion|
      >
        {{!-- prettier-ignore --}}
        <div class='mt8 flex flex-col items-start'>
          {{! Here, the medic is a Care Giver JE. }}
          <LinkToForc
            @feature='care-giver'
            @component='new'
            @patient={{@patient}}
            @sourceJeId={{accordion.medic.id}}
            class='time-label'
            data-test-edit-care-giver
          >
            <i class='pen icon'></i>
            Modifier les informations de ce membre
          </LinkToForc>
{{! @glint-expect-error }}
          <ArchiveJournalEventButton @journalEvent={{accordion.medic}} @confirmationTitle={{concat "Supprimer " this.medic.firstName this.medic.lastName " de l'entourage de soins"}} class='text-[#808080] text-[0.85714286em] hover:text-pink'>
        Retirer ce membre
      </ArchiveJournalEventButton>
        </div>
      </MedicsAccordion>
    {{else}}
      <div class='placeholder-label'>
        Aucun membre dans l'entourage.
      </div>
    {{/if}}
  </template>
}
